import { useEffect, useState } from 'react';
import { AlertTriangle, CloudOff, RefreshCw } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useRealtimeStore } from '@/stores/realtimeStore';
import { useOfflineQueueStore } from '@/stores/offlineQueueStore';
import { useOnlineStatus } from '@/lib/connectivity';
import { cn } from '@/lib/utils';

const ERROR_INDICATOR_DELAY_MS = 4000;

/**
 * The only-when-needed realtime status: an amber "Reconnecting…" pill while
 * the event stream is down, a "Offline · N queued" pill while the device has
 * no network (queued actions sync on reconnect), and a brief "Syncing…" while
 * the offline queue drains. Status is information — it only exists when
 * there's information. (This slot is what remains of the old refresh button.)
 */
export function RealtimeStatusPill({ className }: { className?: string }) {
  const online = useOnlineStatus();
  const queuedCount = useOfflineQueueStore((state) => state.items.length);
  const draining = useOfflineQueueStore((state) => state.draining);
  const reconnecting = useRealtimeOfflineIndicator();

  const queuedSuffix = queuedCount > 0 ? ` · ${queuedCount} queued` : '';
  let content: { icon: React.ReactNode; label: string; tooltip: string } | null = null;
  if (!online) {
    content = {
      icon: <CloudOff className="h-4 w-4" />,
      label: `Offline${queuedSuffix}`,
      tooltip:
        queuedCount > 0
          ? `You're offline. ${queuedCount} action${queuedCount === 1 ? '' : 's'} will sync when the connection returns.`
          : "You're offline. Actions will queue and sync when the connection returns.",
    };
  } else if (draining) {
    content = {
      icon: <RefreshCw className="h-4 w-4 animate-spin" />,
      label: queuedCount > 0 ? `Syncing ${queuedCount}…` : 'Syncing…',
      tooltip: 'Syncing actions taken while offline…',
    };
  } else if (reconnecting) {
    content = {
      icon: <AlertTriangle className="h-4 w-4" />,
      label: `Reconnecting…${queuedSuffix}`,
      tooltip:
        queuedCount > 0
          ? `Realtime connection lost. ${queuedCount} queued action${queuedCount === 1 ? '' : 's'} will sync once it's back.`
          : 'Realtime connection lost. Retrying automatically…',
    };
  } else if (queuedCount > 0) {
    // Online, not draining, queue non-empty: a replay is scheduled (30s
    // retry) — say "queued", not "syncing"; nothing is in flight right now.
    content = {
      icon: <CloudOff className="h-4 w-4" />,
      label: `${queuedCount} queued`,
      tooltip: `${queuedCount} action${queuedCount === 1 ? '' : 's'} waiting for the server — retrying automatically.`,
    };
  }

  if (!content) {
    return null;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            'inline-flex h-8 items-center gap-1.5 rounded-full border border-warning/40 bg-warning/15 px-3 text-warning-ink',
            className
          )}
          role="status"
          aria-live="polite"
          aria-label={content.tooltip}
        >
          {content.icon}
          <span className="hidden text-xs font-medium md:inline">{content.label}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent>{content.tooltip}</TooltipContent>
    </Tooltip>
  );
}

function useRealtimeOfflineIndicator() {
  const realtimeStatus = useRealtimeStore((state) => state.realtimeStatus);
  const showRealtimeConnecting = useRealtimeStore((state) => state.showRealtimeConnecting);
  const [errorDelayElapsed, setErrorDelayElapsed] = useState(false);
  const [indicatorVisible, setIndicatorVisible] = useState(false);

  useEffect(() => {
    if (realtimeStatus !== 'error') {
      setErrorDelayElapsed(false);
      return;
    }

    if (typeof window === 'undefined') {
      setErrorDelayElapsed(true);
      return;
    }

    const timer = window.setTimeout(() => setErrorDelayElapsed(true), ERROR_INDICATOR_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
      setErrorDelayElapsed(false);
    };
  }, [realtimeStatus]);

  useEffect(() => {
    if (realtimeStatus === 'connected' || realtimeStatus === 'idle') {
      setIndicatorVisible(false);
      return;
    }

    if (indicatorVisible) {
      return;
    }

    if (realtimeStatus === 'connecting' && showRealtimeConnecting) {
      setIndicatorVisible(true);
      return;
    }

    if (realtimeStatus === 'error' && errorDelayElapsed) {
      setIndicatorVisible(true);
    }
  }, [indicatorVisible, realtimeStatus, showRealtimeConnecting, errorDelayElapsed]);

  return indicatorVisible;
}
